// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  type ListRenderItem,
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { createTheme } from '../design-system/theme';
import { useColorScheme } from '../foundations/colorScheme';
import { CallHistoryEmptyState } from './CallHistoryEmptyState';
import { CallHistoryFavorites } from './CallHistoryFavorites';
import { CallHistoryFilterBar } from './CallHistoryFilterBar';
import { CallHistoryRow } from './CallHistoryRow';
import { CallHistorySearchBar } from './CallHistorySearchBar';
import { callHistoryStrings } from './strings';
import { CallHistoryDetailsPanel } from './subscreens/CallHistoryDetailsPanel';
import type {
  CallHistoryActions,
  CallHistoryEntry,
  CallHistoryEntryId,
  CallHistoryLoader,
  CallHistorySubscriber,
} from './types';
import { useCallHistoryViewModel } from './useCallHistoryViewModel';

export interface CallHistoryScreenProps {
  readonly load: CallHistoryLoader;
  readonly subscribe: CallHistorySubscriber;
  readonly actions?: CallHistoryActions;
  /** Render the details right-rail when a row is selected. Default true. */
  readonly showDetailsRail?: boolean;
  readonly onItemPress?: (entry: CallHistoryEntry) => void;
}

const ROW_HEIGHT_ESTIMATE = 76;

/** Top-level call-history screen. */
export const CallHistoryScreen: React.FC<CallHistoryScreenProps> = ({
  load,
  subscribe,
  actions,
  showDetailsRail = true,
  onItemPress,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme);
  const vm = useCallHistoryViewModel({
    load,
    subscribe,
    ...(actions !== undefined ? { actions } : {}),
  });

  const [longPressEntryId, setLongPressEntryId] = useState<CallHistoryEntryId | null>(null);
  void longPressEntryId;

  const handleSelect = useCallback(
    (entry: CallHistoryEntry) => {
      vm.selectEntry(entry.summary.id);
      onItemPress?.(entry);
    },
    [onItemPress, vm],
  );

  const handleLongPress = useCallback((entry: CallHistoryEntry) => {
    setLongPressEntryId(entry.summary.id);
  }, []);

  const handleToggleFavorite = useCallback(
    (entry: CallHistoryEntry) => {
      void vm.toggleFavorite(entry.summary.id);
    },
    [vm],
  );

  const keyExtractor = useCallback((item: CallHistoryEntry) => item.summary.id, []);

  const renderItem: ListRenderItem<CallHistoryEntry> = useCallback(
    ({ item }) => (
      <CallHistoryRow
        entry={item}
        selected={vm.selectedEntryId === item.summary.id}
        onPress={handleSelect}
        onLongPress={handleLongPress}
        onToggleFavorite={actions?.onToggleFavorite ? handleToggleFavorite : undefined}
      />
    ),
    [
      actions?.onToggleFavorite,
      handleLongPress,
      handleSelect,
      handleToggleFavorite,
      vm.selectedEntryId,
    ],
  );

  const getItemLayout = useCallback(
    (_: ArrayLike<CallHistoryEntry> | null | undefined, index: number) => ({
      length: ROW_HEIGHT_ESTIMATE,
      offset: ROW_HEIGHT_ESTIMATE * index,
      index,
    }),
    [],
  );

  const selectedEntry = useMemo<CallHistoryEntry | null>(() => {
    if (vm.state.kind !== 'populated' || vm.selectedEntryId === null) {
      return null;
    }
    return vm.state.entries.find((e) => e.summary.id === vm.selectedEntryId) ?? null;
  }, [vm.selectedEntryId, vm.state]);

  const handleCloseRail = useCallback(() => {
    vm.selectEntry(null);
  }, [vm]);

  const renderBody = (): React.ReactElement => {
    if (vm.state.kind === 'loading') {
      return (
        <View style={styles.center}>
          <ActivityIndicator size="small" color={theme.colors.accent.indigo} />
        </View>
      );
    }
    if (vm.state.kind === 'error') {
      return (
        <View style={styles.center}>
          <Text style={[styles.errorTitle, { color: theme.colors.text.primary }]}>
            {callHistoryStrings.errorTitle}
          </Text>
          <Pressable
            onPress={vm.retry}
            accessibilityRole="button"
            accessibilityLabel={callHistoryStrings.errorRetry}
            style={({ pressed }) => [
              styles.retryButton,
              {
                backgroundColor: theme.colors.accent.indigo,
                opacity: pressed ? 0.85 : 1,
              },
            ]}
          >
            <Text style={styles.retryText}>{callHistoryStrings.errorRetry}</Text>
          </Pressable>
        </View>
      );
    }
    if (vm.state.kind === 'empty') {
      return <CallHistoryEmptyState filter={vm.state.filter} query={vm.state.query} />;
    }

    if (vm.filter === 'favorites') {
      return (
        <CallHistoryFavorites
          entries={vm.state.entries}
          selectedEntryId={vm.selectedEntryId}
          onSelect={handleSelect}
          {...(actions?.onToggleFavorite ? { onToggleFavorite: handleToggleFavorite } : {})}
        />
      );
    }

    return (
      <FlatList<CallHistoryEntry>
        data={vm.state.entries}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        getItemLayout={getItemLayout}
        windowSize={10}
        initialNumToRender={15}
        maxToRenderPerBatch={10}
        removeClippedSubviews={false}
      />
    );
  };

  return (
    <View
      style={[styles.container, { backgroundColor: theme.colors.surface.primary }]}
      accessibilityLabel={callHistoryStrings.screenTitle}
    >
      <View style={styles.main}>
        <View style={[styles.header, { borderBottomColor: theme.colors.border.secondary }]}>
          <Text style={[styles.title, { color: theme.colors.text.primary }]}>
            {callHistoryStrings.screenTitle}
          </Text>
        </View>
        <CallHistorySearchBar value={vm.query} onChange={vm.setQuery} />
        <CallHistoryFilterBar active={vm.filter} onChange={vm.setFilter} />
        {renderBody()}
      </View>
      {showDetailsRail && selectedEntry !== null ? (
        <CallHistoryDetailsPanel
          entry={selectedEntry}
          {...(actions !== undefined ? { actions } : {})}
          onClose={handleCloseRail}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  } satisfies ViewStyle,
  main: {
    flex: 1,
  } satisfies ViewStyle,
  header: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  title: {
    fontSize: 17,
    fontWeight: '600',
  } satisfies TextStyle,
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    gap: 12,
  } satisfies ViewStyle,
  errorTitle: {
    fontSize: 17,
    fontWeight: '600',
    textAlign: 'center',
  } satisfies TextStyle,
  retryButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  } satisfies ViewStyle,
  retryText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  } satisfies TextStyle,
});
